
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Save, MapPin, Map as MapIcon, Info, Check, AlertTriangle, MessageSquare, ExternalLink, ShieldCheck, ArrowRight, Lock } from 'lucide-react';
import { api, Store, Staff, isLineEnabled } from '../../api';
import Input from '../ui/Input';
import Dialog from '../ui/Dialog';
import { useToast } from '../../contexts/ToastContext';


interface GeneralSettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
  currentUser: { uid: string } | null;
  onStoresChange: (stores: Store[]) => void;
}

const GeneralSettingsTab: React.FC<GeneralSettingsTabProps> = ({ stores: initialStores, myProfile, isSystemAdmin, currentUser, onStoresChange }) => {
  const { showToast } = useToast();
  const [localStores, setLocalStores] = useState<Store[]>(initialStores);
  const [companyName, setCompanyName] = useState('');
  const [originalCompanyName, setOriginalCompanyName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [staffCount, setStaffCount] = useState(0);
  const [dialog, setDialog] = useState<{ isOpen: boolean, type: 'alert' | 'confirm' | 'prompt' | 'info', title: string, message: string, onConfirm: (value?: string) => void, isDestructive?: boolean }>({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: () => {} });

  const inputClass = "w-full bg-white border border-slate-300 rounded-[4px] px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all shadow-sm font-bold";

  useEffect(() => {
    // 親の店舗一覧の読み込み完了後に反映する（編集中の未保存内容は上書きしない）。
    if (!hasChanges) setLocalStores(initialStores);
    if (initialStores.length > 0) {
      setCompanyName(initialStores[0].companyName || '');
      setOriginalCompanyName(initialStores[0].companyName || '');
    }
    
    const fetchStaffCount = async () => {
      try {
        const staff = await api.getStaff();
        const count = staff.filter(s => !s.isGuest && s.status !== 'resigned').length;
        setStaffCount(count > 0 ? count : 1);
      } catch (e) {
        console.error('Failed to fetch staff count', e);
      }
    };
    fetchStaffCount();
  }, [initialStores]);

  const updateStoreState = (id: string, data: Partial<Store>) => {
    setLocalStores(prev => prev.map(s => s.id === id ? { ...s, ...data } : s));
    setHasChanges(true);
  };

  const handleSaveCompany = async () => {
    if (!companyName.trim()) return;
    setIsLoading(true);
    try {
      await api.updateCompanyName(companyName);
      setOriginalCompanyName(companyName);
      showToast('企業情報を保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveStores = async () => {
    setIsLoading(true);
    try {
      await api.saveStores(localStores);
      setHasChanges(false);
      onStoresChange(localStores);
      showToast('店舗情報を保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStore = () => {
    setDialog({
      isOpen: true, type: 'prompt', title: '新規店舗追加', message: '店舗名を入力してください。',
      onConfirm: async (name?: string) => {
        if (!name) return;
        setIsLoading(true);
        try {
          const newStore: Store = {
            id: `store_${Date.now()}`, name, companyName, ownerId: currentUser?.uid || '', plan: 'standard',
            createdAt: new Date().toISOString(), submissionCycle: 'monthly', baseStartTime: '09:00', baseEndTime: '22:00',
            allowDigitalAbsence: false
          };
          await api.saveStores([...localStores, newStore]);
          if (myProfile) {
            const updatedProfile = { ...myProfile, stores: Array.from(new Set([...(myProfile.stores || []), newStore.id])) };
            await api.saveStaff([updatedProfile]);
          }
          setLocalStores(prev => [...prev, newStore]);
          onStoresChange([...localStores, newStore]);
          showToast('店舗を追加しました。');
        } catch (e) { showToast('追加に失敗しました。', 'error'); }
        finally { setIsLoading(false); setDialog({ ...dialog, isOpen: false }); }
      }
    });
  };

  // --- Stripe 設定エリア ---
  const STRIPE_PRICE_IDS = {
    standard: 'price_1SexAFKVuJTgCcudbPKJBkDI',
    business: 'price_1SexAoKVuJTgCcudKvIIXlxq'
  };

  const handlePlanChange = async (plan: 'standard' | 'business') => {
      const store = localStores[0];
      if (!store) return;
      setIsRedirecting(true);
      try {
          const url = store.stripeSubscriptionId
              ? await api.createPortalSession(store.id)
              : await api.createCheckoutSession(store.id, STRIPE_PRICE_IDS[plan], staffCount);
          window.location.href = url;
      } catch (e: unknown) {
          console.error('Stripe transition error:', e);
          setIsRedirecting(false);
          showToast(`決済画面への移動に失敗しました。${e instanceof Error ? e.message : ''}`, 'error');
      }
  };

  const firstStore = localStores[0];
  const isCurrentlyTrial = firstStore && !firstStore.stripeSubscriptionId;

  return (
    <div className="space-y-8 animate-fade-in-up pb-20">
      <Dialog {...dialog} onCancel={() => setDialog({ ...dialog, isOpen: false })} />
      
      {/* 企業情報 */}
      <section className="bg-white p-8 border border-gray-100 shadow-sm rounded-[4px]">
        <div className="flex justify-between items-center mb-6 border-l-4 border-blue-600 pl-4">
          <h2 className="text-lg font-bold text-slate-800">企業情報</h2>
          <button onClick={handleSaveCompany} disabled={isLoading || companyName === originalCompanyName} className={`font-bold px-6 py-2 rounded text-sm shadow-md flex items-center transition-all ${companyName !== originalCompanyName ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save className="mr-2" size={16} />}保存</button>
        </div>
        <div className="max-w-md"><Input label="企業名" value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></div>
      </section>

      {/* 店舗一覧 */}
      <section className="bg-white p-8 border border-gray-100 shadow-sm rounded-[4px]">
        <div className="flex justify-between items-center mb-6 border-l-4 border-blue-600 pl-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-slate-800">店舗一覧・運用設定</h2>
            {hasChanges && <span className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full border border-amber-200"><AlertTriangle size={12} /> 未保存</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddStore} className="bg-white border border-slate-300 text-slate-600 font-bold px-4 py-2 rounded text-sm flex items-center hover:bg-slate-50 transition-all"><Plus size={16} className="mr-1"/>店舗追加</button>
            <button onClick={handleSaveStores} disabled={!hasChanges || isLoading} className={`font-bold px-6 py-2 rounded text-sm shadow-md flex items-center transition-all ${hasChanges ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save className="mr-2" size={16} />}保存</button>
          </div>
        </div>
        
        <div className="space-y-4">
          {localStores.map(store => (
            <div key={store.id} className="bg-slate-50 border border-slate-200 rounded p-6">
              <div className="flex flex-wrap items-start gap-8">
                <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">店舗名</label><input type="text" value={store.name} onChange={(e) => updateStoreState(store.id, { name: e.target.value })} className={inputClass} /></div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">欠勤申請の許可（アプリ内）</label>
                            <button 
                                onClick={() => updateStoreState(store.id, { allowDigitalAbsence: !store.allowDigitalAbsence })}
                                className={`flex items-center gap-2 px-3 py-2 rounded-[4px] border font-bold text-xs transition-all ${store.allowDigitalAbsence ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-white border-slate-300 text-slate-400'}`}
                            >
                                {store.allowDigitalAbsence ? '許可する（SOS自動送信）' : '許可しない（電話連絡を促す）'}
                            </button>
                        </div>
                    </div>
                    <div data-testid={`line-setting-${store.id}`}>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">スタッフへの連絡手段</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {[
                                { value: true, title: 'LINEを使う', desc: 'スタッフはLINEから連携してログインします。提出依頼・確定・SOSはLINEで届きます。' },
                                { value: false, title: 'LINEを使わない', desc: 'スタッフはメールの招待からID・パスワードを作成してログインします。通知はメールとアプリ内のお知らせで届きます。' },
                            ].map(opt => {
                                const selected = isLineEnabled(store) === opt.value;
                                return (
                                    <button key={String(opt.value)} type="button" onClick={() => updateStoreState(store.id, { lineEnabled: opt.value })}
                                        className={`text-left p-3 rounded-[4px] border transition-all ${selected ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-100' : 'bg-white border-slate-300 hover:border-slate-400'}`}>
                                        <span className={`flex items-center gap-2 text-sm font-bold ${selected ? 'text-blue-700' : 'text-slate-700'}`}>
                                            <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-blue-600' : 'border-slate-300'}`}>{selected && <span className="w-2 h-2 rounded-full bg-blue-600" />}</span>
                                            {opt.title}
                                        </span>
                                        <span className="block text-[11px] text-slate-500 leading-relaxed mt-1">{opt.desc}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">シフト提出期限（毎月○日）</label>
                            <input type="number" min={1} max={31} value={store.submitDeadline || ''} placeholder="例：20" onChange={(e) => updateStoreState(store.id, { submitDeadline: e.target.value ? Number(e.target.value) : undefined })} className={inputClass} />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">締め日（毎月○日）</label>
                            <input type="number" min={1} max={31} value={store.closingDate || ''} placeholder="例：25" onChange={(e) => updateStoreState(store.id, { closingDate: e.target.value ? Number(e.target.value) : undefined })} className={inputClass} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor={`min-shift-${store.id}`} className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">1回の最短勤務時間（自動作成）</label>
                        <select id={`min-shift-${store.id}`} value={store.minShiftMinutes || 120} onChange={(e) => updateStoreState(store.id, { minShiftMinutes: Number(e.target.value) })} className={inputClass}>
                            {[60, 90, 120, 150, 180, 240].map(m => <option key={m} value={m}>{m % 60 === 0 ? `${m / 60}時間` : `${Math.floor(m / 60)}時間${m % 60}分`}</option>)}
                        </select>
                        <p className="text-[11px] text-slate-500 mt-1">自動作成では、これより短い時間の割り当てはしません。時間帯ごとに足りない分を、希望の時間に合わせて組み合わせます。</p>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">住所（打刻範囲の基準・郵便番号は不要です）</label>
                        <div className="flex items-center gap-2">
                            <input type="text" placeholder="例：東京都渋谷区道玄坂1-1-1" value={store.location?.address || ''} onChange={(e) => updateStoreState(store.id, { location: { ...store.location!, address: e.target.value } })} className={inputClass} />
                            {store.location?.lat && <a href={`https://www.google.com/maps/search/?api=1&query=${store.location.lat},${store.location.lng}`} target="_blank" rel="noreferrer" className="p-2.5 bg-blue-50 text-blue-600 border border-blue-100 rounded hover:bg-blue-100"><MapIcon size={18} /></a>}
                        </div>
                    </div>
                </div>
                <button onClick={() => {
                  setDialog({
                    isOpen: true, type: 'confirm', title: '店舗削除', message: `「${store.name}」を削除しますか？\n関連するシフト・勤怠・設定データも全て削除されます。この操作は取り消せません。`, isDestructive: true,
                    onConfirm: async () => {
                      try {
                        await api.clearStoreData(store.id);
                        await api.deleteStore(store.id);
                        setLocalStores(prev => prev.filter(s => s.id !== store.id));
                        onStoresChange?.(localStores.filter(s => s.id !== store.id));
                        showToast('店舗を削除しました。', 'success');
                      } catch (e) {
                        showToast('店舗の削除に失敗しました。', 'error');
                      }
                      setDialog({ ...dialog, isOpen: false });
                    }
                  })
                }} className="p-2 text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={20}/></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* プラン設定 */}
      <section className="bg-white p-8 border border-gray-100 shadow-sm rounded-[4px]">
        <div className="flex justify-between items-center mb-10 border-l-4 border-blue-600 pl-4">
          <h2 className="text-lg font-bold text-slate-800">プラン設定</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <PlanCard 
            title="無料トライアル" 
            price="0円" 
            recommendation="まずは何ができるか試してみたい方"
            features={["ビジネスプランのすべての機能を\n60日間無料利用可能"]}
            planType="trial" 
            isApplied={isCurrentlyTrial}
          />
          <PlanCard 
            title="スタンダード" 
            price="100円" 
            recommendation="シフト作成・管理だけを楽にしたい方"
            features={[
                "シフト作成・調整・提出・共有",
                "欠勤時の自動代理人探し",
                "シフト表の印刷・PDF出力",
                "LINE連携・通知（LINEを使わない運用も可）"
            ]}
            planType="standard" 
            isApplied={!isCurrentlyTrial && firstStore?.plan === 'standard'}
            onSelect={() => !isRedirecting && handlePlanChange('standard')}
          />
          <PlanCard 
            title="ビジネス" 
            price="300円" 
            recommendation="給与計算まで効率化したい方"
            features={[
                "スタンダードプランのすべて",
                "勤怠管理・打刻機能",
                "勤怠データのCSV出力"
            ]}
            planType="business" 
            isApplied={!isCurrentlyTrial && firstStore?.plan === 'business'}
            onSelect={() => !isRedirecting && handlePlanChange('business')}
          />
        </div>

        {/* セットアップサポート */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 mb-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="text-blue-600" size={24} />
                        <h3 className="text-lg font-black text-slate-800">初期セットアップサポートをご希望の方はこちら</h3>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed font-bold">
                        シフトメイトの基本的な操作方法をはじめ、情報登録の仕方等、初期のセットアップに必要なこと、お困りごとの解消をオンラインでサポートします。
                    </p>
                </div>
                <div className="text-center md:text-right shrink-0">
                    <div className="mb-3">
                        <span className="text-[10px] font-black text-slate-400 block uppercase tracking-widest">初期セットアップサポート</span>
                        <span className="text-2xl font-black text-slate-800">50,000円</span>
                        <span className="text-xs font-bold text-slate-400 ml-1">（税別）</span>
                    </div>
                    <a 
                        href="https://lp.shiftmate.jp/contact" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block bg-slate-800 text-white px-8 py-3 rounded-xl font-bold text-sm hover:bg-black transition-all shadow-lg active:scale-95"
                    >
                        サポートを申し込む
                    </a>
                </div>
            </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-start gap-5 shadow-sm">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0 border border-blue-100">
                <Info size={24} />
            </div>
            <div className="space-y-2">
                <h4 className="font-black text-slate-800">料金計算と決済に関するご注意</h4>
                <ul className="space-y-1.5">
                    <li className="text-xs font-bold text-slate-500 flex items-center gap-2">
                        <div className="w-1 h-1 bg-slate-400 rounded-full" />
                        料金は「登録済みの総スタッフ数」に基づいて、毎月末に確定し決済されます。（現在の登録：{staffCount}名）
                    </li>
                    <li className="text-xs font-bold text-slate-500 flex items-center gap-2">
                        <div className="w-1 h-1 bg-slate-400 rounded-full" />
                        決済情報の登録・管理は、決済サービス「Stripe」を使用しています。プラン変更時は残り日数に応じて自動で日割り計算されます。
                    </li>
                </ul>
            </div>
        </div>
      </section>
    </div>
  );
};

const PlanCard = ({ title, price, recommendation, features, isApplied, onSelect }: { title: string, price: string, recommendation?: string, features: string[], planType?: string, isApplied?: boolean, onSelect?: () => void }) => {
  return (
    <div className={`p-6 rounded-2xl border-2 transition-all flex flex-col h-full ${isApplied ? 'border-blue-600 bg-blue-50/30 ring-4 ring-blue-100/20' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
      <h3 className="text-xl font-black text-slate-800 mb-2">{title}</h3>
      <div className="mb-4 flex items-baseline gap-1">
          <span className="text-3xl font-black text-slate-900">{price}</span>
          <span className="text-xs font-bold text-slate-400">/ 月・人（税別）</span>
      </div>
      
      {recommendation && (
        <div className="mb-6 p-2.5 bg-slate-100 rounded-lg text-[10px] font-black text-slate-600 leading-normal">
            <span className="text-blue-600 mr-1">●</span>こんな方におすすめ<br/>
            {recommendation}
        </div>
      )}

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((f: string, i: number) => (
            <li key={i} className="flex items-start gap-2.5 text-xs font-bold text-slate-600 leading-snug whitespace-pre-wrap">
                <Check size={14} className="text-blue-500 mt-0.5 shrink-0" strokeWidth={3} />
                {f}
            </li>
        ))}
      </ul>

      <button 
        onClick={onSelect}
        disabled={isApplied} 
        className={`w-full py-4 rounded-xl font-black text-sm transition-all active:scale-95 ${isApplied ? 'bg-white text-slate-400 border border-slate-200 cursor-default' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'}`}
      >
        {isApplied ? '適用中' : '変更する'}
      </button>
    </div>
  );
};

export default GeneralSettingsTab;
